import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  FaTruck, FaCheckCircle, FaTimesCircle, FaMoneyBillWave, 
  FaMapMarkerAlt, FaClock, FaToggleOn, FaToggleOff, FaPhone, FaEnvelope 
} from 'react-icons/fa';
import './DeliveryPartnerDashboard.css';

const DeliveryPartnerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [availability, setAvailability] = useState('offline');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (!user || user.userType !== 'delivery_partner') {
      navigate('/');
      return;
    }
    
    fetchDashboardData();
    
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Single comprehensive API call instead of 4+ separate calls
      const response = await api.get('/delivery-partner/dashboard');
      const data = response.data;

      console.log('Dashboard data:', data);

      // Extract data from comprehensive response
      setStats({
        ...data.stats,
        todayDeliveries: data.earnings.today.deliveries,
        todayEarnings: data.earnings.today.earnings,
        availability: data.partner.availability
      });
      
      setActiveDelivery(data.activeDelivery);
      setAvailableDeliveries(data.availableDeliveries || []);
      
      // Transform earnings to match component expectations
      setEarnings({
        today: data.earnings.today,
        week: data.earnings.week,
        month: data.earnings.month,
        lifetime: {
          earnings: data.earnings.total.earnings,
          deliveries: data.stats.completedDeliveries
        }
      });
      
      setAvailability(data.partner.availability);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async () => {
    try {
      const response = await api.post('/delivery-partner/availability/toggle');
      setAvailability(response.data.partner.availability.status);
      toast.success(`You are now ${response.data.partner.availability.status}`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast.error(error.response?.data?.message || 'Failed to update availability');
    }
  };

  const acceptDelivery = async (deliveryId) => {
    try {
      await api.post(`/delivery-partner/deliveries/${deliveryId}/accept`);
      toast.success('Delivery accepted! Check Active Delivery tab');
      fetchDashboardData();
      setActiveTab('active');
    } catch (error) {
      console.error('Error accepting delivery:', error);
      toast.error(error.response?.data?.message || 'Failed to accept delivery');
    }
  };

  const rejectDelivery = async (deliveryId, reason) => {
    try {
      await api.post(`/delivery-partner/deliveries/${deliveryId}/reject`, {
        rejectionReason: reason || 'Not available'
      });
      toast.success('Delivery rejected');
      fetchDashboardData();
    } catch (error) {
      console.error('Error rejecting delivery:', error);
      toast.error(error.response?.data?.message || 'Failed to reject delivery');
    }
  };

  const updateDeliveryStatus = async (deliveryId, newStatus) => {
    try {
      await api.post(`/delivery-partner/deliveries/${deliveryId}/update-status`, {
        status: newStatus
      });
      toast.success(`Status updated to: ${newStatus.replace(/_/g, ' ')}`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'partner_accepted': 'pickup_in_progress',
      'pickup_in_progress': 'picked_up',
      'picked_up': 'in_transit',
      'in_transit': 'out_for_delivery',
      'out_for_delivery': 'delivered'
    };
    return statusFlow[currentStatus];
  };

  const getStatusLabel = (status) => {
    const labels = {
      'partner_accepted': 'Go to Pickup',
      'pickup_in_progress': 'Mark as Picked Up',
      'picked_up': 'Start Transit',
      'in_transit': 'Out for Delivery',
      'out_for_delivery': 'Mark as Delivered'
    };
    return labels[status] || 'Update Status';
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading your dashboard...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delivery-partner-dashboard">
        <div className="dashboard-header">
          <div className="header-left">
            <h1>🏍️ Delivery Partner Portal</h1>
            <p>Welcome back, {user?.businessName}!</p>
          </div>
          <div className="header-right">
            <button
              className={`availability-toggle ${availability}`}
              onClick={toggleAvailability}
            >
              {availability === 'online' ? <FaToggleOn /> : <FaToggleOff />}
              <span>{availability === 'online' ? 'Online' : 'Offline'}</span>
            </button>
          </div>
        </div>

        {/* Earnings Summary */}
        <div className="earnings-summary">
          <div className="earning-card today">
            <div className="earning-icon">
              <FaMoneyBillWave />
            </div>
            <div className="earning-info">
              <h3>₹{earnings?.today?.earnings || 0}</h3>
              <p>Today's Earnings</p>
            </div>
          </div>

          <div className="earning-card week">
            <div className="earning-icon">
              <FaMoneyBillWave />
            </div>
            <div className="earning-info">
              <h3>₹{earnings?.week?.earnings || 0}</h3>
              <p>This Week</p>
            </div>
          </div>

          <div className="earning-card month">
            <div className="earning-icon">
              <FaMoneyBillWave />
            </div>
            <div className="earning-info">
              <h3>₹{earnings?.month?.earnings || 0}</h3>
              <p>This Month</p>
            </div>
          </div>

          <div className="earning-card lifetime">
            <div className="earning-icon">
              <FaCheckCircle />
            </div>
            <div className="earning-info">
              <h3>{earnings?.lifetime?.deliveries || stats?.completedDeliveries || 0}</h3>
              <p>Total Deliveries</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <FaTruck /> Active Delivery
          </button>
          <button
            className={`tab ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            <FaMapMarkerAlt /> Available ({availableDeliveries.length})
          </button>
          <button
            className={`tab ${activeTab === 'earnings' ? 'active' : ''}`}
            onClick={() => setActiveTab('earnings')}
          >
            <FaMoneyBillWave /> Earnings
          </button>
        </div>

        {/* Content */}
        <div className="dashboard-content">
          {activeTab === 'active' && (
            <div className="active-delivery-section">
              {!activeDelivery ? (
                <div className="empty-state">
                  <FaTruck />
                  <h3>No Active Delivery</h3>
                  <p>Accept a delivery from Available Deliveries to get started</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveTab('available')}
                  >
                    View Available Deliveries
                  </button>
                </div>
              ) : (
                <div className="active-delivery-card">
                  <div className="delivery-header">
                    <h2>🚚 Active Delivery</h2>
                    <span className={`status-badge ${activeDelivery.status}`}>
                      {activeDelivery.status.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="delivery-info-grid">
                    <div className="info-card">
                      <h4>Delivery Details</h4>
                      <p><strong>Delivery #:</strong> {activeDelivery.deliveryNumber}</p>
                      <p><strong>Order #:</strong> {activeDelivery.order?.orderNumber}</p>
                      <p><strong>Distance:</strong> {activeDelivery.estimatedDistance || 0} km</p>
                      <p><strong>Fee:</strong> ₹{activeDelivery.deliveryFee}</p>
                    </div>

                    <div className="info-card seller">
                      <h4>📦 Pickup from Seller</h4>
                      <p><strong>{activeDelivery.order?.seller?.businessName}</strong></p>
                      <p className="contact">
                        <FaPhone /> {activeDelivery.order?.seller?.phone}
                      </p>
                      <p className="contact">
                        <FaEnvelope /> {activeDelivery.order?.seller?.email}
                      </p>
                      <div className="address">
                        <FaMapMarkerAlt />
                        <span>{activeDelivery.pickupLocation?.address}</span>
                      </div>
                    </div>

                    <div className="info-card buyer">
                      <h4>🏠 Deliver to Buyer</h4>
                      <p><strong>{activeDelivery.order?.buyer?.businessName}</strong></p>
                      <p className="contact">
                        <FaPhone /> {activeDelivery.order?.buyer?.phone}
                      </p>
                      <p className="contact">
                        <FaEnvelope /> {activeDelivery.order?.buyer?.email}
                      </p>
                      <div className="address">
                        <FaMapMarkerAlt />
                        <span>{activeDelivery.deliveryLocation?.address}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status Update Button */}
                  {activeDelivery.status !== 'delivered' && (
                    <div className="status-actions">
                      <button
                        className="btn btn-success btn-lg"
                        onClick={() => updateDeliveryStatus(
                          activeDelivery._id,
                          getNextStatus(activeDelivery.status)
                        )}
                      >
                        <FaCheckCircle /> {getStatusLabel(activeDelivery.status)}
                      </button>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="delivery-timeline">
                    <h4>📍 Delivery Timeline</h4>
                    <div className="timeline">
                      {activeDelivery.trackingHistory?.map((track, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-dot"></div>
                          <div className="timeline-content">
                            <p className="timeline-status">{track.status.replace(/_/g, ' ')}</p>
                            <p className="timeline-time">
                              <FaClock /> {new Date(track.timestamp).toLocaleString()}
                            </p>
                            {track.notes && <p className="timeline-notes">{track.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'available' && (
            <div className="available-deliveries-section">
              <h2>Available Delivery Requests</h2>
              {availability !== 'online' && (
                <div className="warning-banner">
                  ⚠️ You are currently offline. Turn online to accept deliveries.
                </div>
              )}
              
              {availableDeliveries.length === 0 ? (
                <div className="empty-state">
                  <FaMapMarkerAlt />
                  <h3>No Available Deliveries</h3>
                  <p>New delivery requests will appear here</p>
                </div>
              ) : (
                <div className="deliveries-grid">
                  {availableDeliveries.map(delivery => (
                    <div key={delivery._id} className="delivery-request-card">
                      <div className="card-header">
                        <h3>Delivery #{delivery.deliveryNumber}</h3>
                        <span className="distance-badge">
                          📍 {delivery.distance?.toFixed(1) || 0} km away
                        </span>
                      </div>

                      <div className="card-body">
                        <div className="delivery-detail">
                          <strong>Pickup:</strong>
                          <p>{delivery.pickupLocation?.address}</p>
                        </div>
                        <div className="delivery-detail">
                          <strong>Delivery:</strong>
                          <p>{delivery.deliveryLocation?.address}</p>
                        </div>
                        <div className="delivery-stats">
                          <span className="stat">
                            <FaTruck /> {delivery.estimatedDistance || 0} km
                          </span>
                          <span className="stat earning">
                            <FaMoneyBillWave /> ₹{delivery.deliveryFee}
                          </span>
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          className="btn btn-success"
                          onClick={() => acceptDelivery(delivery._id)}
                          disabled={availability !== 'online'}
                        >
                          <FaCheckCircle /> Accept
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => rejectDelivery(delivery._id)}
                        >
                          <FaTimesCircle /> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'earnings' && (
            <div className="earnings-section">
              <h2>💰 Earnings Summary</h2>
              
              <div className="earnings-breakdown">
                <div className="breakdown-card">
                  <h3>Today</h3>
                  <p className="amount">₹{earnings?.today?.earnings || 0}</p>
                  <p className="deliveries">{earnings?.today?.deliveries || 0} deliveries</p>
                </div>

                <div className="breakdown-card">
                  <h3>This Week</h3>
                  <p className="amount">₹{earnings?.week?.earnings || 0}</p>
                  <p className="deliveries">{earnings?.week?.deliveries || 0} deliveries</p>
                </div>

                <div className="breakdown-card">
                  <h3>This Month</h3>
                  <p className="amount">₹{earnings?.month?.earnings || 0}</p>
                  <p className="deliveries">{earnings?.month?.deliveries || 0} deliveries</p>
                </div>

                <div className="breakdown-card lifetime">
                  <h3>Lifetime Earnings</h3>
                  <p className="amount">₹{earnings?.lifetime?.earnings || 0}</p>
                  <p className="deliveries">{earnings?.lifetime?.deliveries || stats?.completedDeliveries || 0} total deliveries</p>
                </div>
              </div>

              <div className="balance-card">
                <h3>Available Balance</h3>
                <p className="balance">₹{earnings?.lifetime?.earnings || 0}</p>
                <button className="btn btn-primary">
                  Request Withdrawal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DeliveryPartnerDashboard;
